import { useId, type FormEvent } from 'react'
import TextField from './TextField'
import { buttonClass, eyebrowClass, inputClass } from './ui'

export type ProfileData = {
  swing_speed_mph: string
  attack_angle_deg: string
  typical_miss: string
  current_driver_head: string
  current_driver_shaft: string
  handicap: string
  goals: string
}

type Props = {
  data: ProfileData
  errors: Partial<Record<keyof ProfileData, string>>
  missOptions: string[]
  processing: boolean
  onChange: (field: keyof ProfileData, value: string) => void
  onSubmit: () => void
}

export default function ProfileForm({ data, errors, missOptions, processing, onChange, onSubmit }: Props) {
  const id = useId()
  const missId = `miss-${id}`
  const goalsId = `goals-${id}`

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!processing) onSubmit()
  }

  return (
    <form onSubmit={handleSubmit} aria-busy={processing} className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="space-y-8 p-6 sm:p-8">
        <fieldset>
          <legend className="mb-5 flex items-center gap-3 font-serif text-2xl">
            <span className={`${eyebrowClass} text-muted`}>01</span> Your swing
          </legend>
          <p className="mb-5 text-sm leading-6 text-muted">Use numbers you know. If you haven’t measured something, leave it blank.</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Driver swing speed (mph)" name="swing_speed_mph" type="number" step="0.1"
              value={data.swing_speed_mph} error={errors.swing_speed_mph} onChange={(v) => onChange('swing_speed_mph', v)} />
            <TextField label="Attack angle with driver (degrees, if known)" name="attack_angle_deg" type="number" step="0.1"
              value={data.attack_angle_deg} error={errors.attack_angle_deg} onChange={(v) => onChange('attack_angle_deg', v)} />
            <div className="space-y-2">
              <label htmlFor={missId} className="block text-sm font-medium">Typical miss</label>
              <select id={missId} name="typical_miss" className={inputClass}
                value={data.typical_miss} onChange={(e) => onChange('typical_miss', e.target.value)}
                aria-invalid={errors.typical_miss ? true : undefined}
                aria-describedby={errors.typical_miss ? `${missId}-error` : undefined}>
                <option value="">Not sure</option>
                {missOptions.map((miss) => <option key={miss} value={miss}>{miss}</option>)}
              </select>
              {errors.typical_miss && <p id={`${missId}-error`} className="text-sm leading-relaxed text-alert-ink">{errors.typical_miss}</p>}
            </div>
            <TextField label="Handicap" name="handicap" type="number" step="0.1"
              value={data.handicap} error={errors.handicap} onChange={(v) => onChange('handicap', v)} />
          </div>
        </fieldset>
        <div className="border-t border-line pt-8">
          <fieldset>
            <legend className="mb-5 flex items-center gap-3 font-serif text-2xl">
              <span className={`${eyebrowClass} text-muted`}>02</span> What’s in your bag
            </legend>
            <p className="mb-5 text-sm leading-6 text-muted">Start with your current driver. Add the model and any details you know.</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField label="Current driver head" name="current_driver_head"
                value={data.current_driver_head} error={errors.current_driver_head} onChange={(v) => onChange('current_driver_head', v)} />
              <TextField label="Current driver shaft" name="current_driver_shaft"
                value={data.current_driver_shaft} error={errors.current_driver_shaft} onChange={(v) => onChange('current_driver_shaft', v)} />
            </div>
          </fieldset>
        </div>
        <div className="border-t border-line pt-8">
          <fieldset>
            <legend className="mb-5 flex items-center gap-3 font-serif text-2xl">
              <span className={`${eyebrowClass} text-muted`}>03</span> Your next step
            </legend>
            <div className="space-y-2">
              <label htmlFor={goalsId} className="block text-sm font-medium">What do you want to improve?</label>
              <p id={`${goalsId}-hint`} className="text-sm leading-6 text-muted">More consistent contact, a miss you want to understand, or simply making sense of your shaft options.</p>
              <textarea id={goalsId} name="goals" rows={4} className={inputClass}
                value={data.goals} onChange={(e) => onChange('goals', e.target.value)}
                aria-invalid={errors.goals ? true : undefined}
                aria-describedby={errors.goals ? `${goalsId}-error` : `${goalsId}-hint`} />
              {errors.goals && <p id={`${goalsId}-error`} className="text-sm leading-relaxed text-alert-ink">{errors.goals}</p>}
            </div>
          </fieldset>
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-line bg-paper px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <p className="text-xs text-muted">Everything is optional. Update it any time.</p>
        <button type="submit" className={buttonClass} disabled={processing}>
          {processing ? 'Saving profile…' : 'Save profile'}
        </button>
      </div>
    </form>
  )
}
